import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DataList, Field, PasswordAndTwoFactor, PhoneSetup, SecretReveal, SessionsPanel, describeUserAgent, type DataListColumn } from "@ozpanel/kit";
import { Button, Details, Section, formatRelative, useConfirm, useToast } from "@ozpanel/ui";
import { useMailboxApi, type AppPassword, type Device } from "../api";

// Everything about getting IN: the two passwords, what is signed in, two-factor, and keys.
//
// Ported from v1's mailbox.html, which keeps THE TWO PASSWORDS APART and is right to. They are
// genuinely different credentials for different things — one signs into this page, one is what a
// mail app uses — and a console that blurs them produces someone changing the wrong one and
// concluding the system is broken. Each says what it is for, at the point of changing it.

function PasswordPair({
  title,
  hint,
  currentLabel,
  onSave,
  minLength,
}: {
  title: string;
  hint: React.ReactNode;
  currentLabel: string;
  onSave: (current: string, next: string) => Promise<unknown>;
  minLength: number;
}) {
  const { notify } = useToast();
  const [cur, setCur] = React.useState("");
  const [nw, setNw] = React.useState("");
  const [again, setAgain] = React.useState("");

  const m = useMutation({
    mutationFn: () => onSave(cur, nw),
    onSuccess: () => {
      setCur("");
      setNw("");
      setAgain("");
      notify({ kind: "success", title: `${title} changed` });
    },
    onError: (e: unknown) =>
      notify({ kind: "error", title: `Could not change ${title.toLowerCase()}`, body: e instanceof Error ? e.message : String(e) }),
  });

  const mismatch = again !== "" && nw !== again;
  const tooShort = nw !== "" && nw.length < minLength;

  return (
    <Section title={title}>
      <div className="space-y-3">
        <div className="text-[13px] leading-relaxed text-muted">{hint}</div>
        <Field label={currentLabel} type="password" autoComplete="current-password" value={cur} onChange={setCur} />
        <Field label={`New password (at least ${minLength} characters)`} type="password" autoComplete="new-password" value={nw} onChange={setNw} />
        <Field label="New password again" type="password" autoComplete="new-password" value={again} onChange={setAgain} />
        {mismatch && <div className="text-[12px] text-crit">The two new passwords do not match.</div>}
        {tooShort && <div className="text-[12px] text-crit">At least {minLength} characters.</div>}
        <Button
          variant="accent"
          onClick={() => m.mutate()}
          disabled={m.isPending || !cur || !nw || mismatch || tooShort}
        >
          {m.isPending ? "Changing…" : `Change ${title.toLowerCase()}`}
        </Button>
      </div>
    </Section>
  );
}

export function Security() {
  const api = useMailboxApi();
  const qc = useQueryClient();
  const { notify } = useToast();

  const devices = useQuery({ queryKey: ["devices"], queryFn: api.devices });
  const appPw = useQuery({ queryKey: ["app-passwords"], queryFn: api.appPasswords });
  const tokens = useQuery({ queryKey: ["auth-tokens"], queryFn: api.authTokens, retry: false });
  const myTokens = useQuery({ queryKey: ["my-tokens"], queryFn: api.myTokens, retry: false });

  const confirm = useConfirm();
  const [newAppName, setNewAppName] = React.useState("");
  const [revealed, setRevealed] = React.useState<
    { what: string; value: string; username?: string; note?: string } | null
  >(null);
  const act = async (label: string, fn: () => Promise<unknown>, keys: string[]) => {
    try {
      await fn();
      notify({ kind: "success", title: label });
      for (const k of keys) void qc.invalidateQueries({ queryKey: [k] });
    } catch (e) {
      notify({ kind: "error", title: `${label} failed`, body: e instanceof Error ? e.message : String(e) });
    }
  };

  const deviceCols: DataListColumn<Device>[] = [
    { key: "user_agent", label: "App", phone: "title", render: (d) => describeUserAgent(d.user_agent) },
    { key: "service", label: "Using", sortable: true, render: (d) => (d.service || "").toUpperCase() },
    { key: "ip", label: "From", render: (d) => <span className="font-mono text-[12.5px]">{d.ip}</span> },
  ];

  const appCols: DataListColumn<AppPassword>[] = [
    { key: "name", label: "Name", sortable: true },
    {
      // Recoverable later. The password is not, but the username is — and without it a working
      // credential is unusable because nobody remembers the tag they typed.
      key: "username", label: "Signs in as",
      render: (a: AppPassword) => <span className="break-all font-mono text-[11.5px]">{a.username ?? "—"}</span>,
    },
    {
      key: "created_at",
      label: "Created",
      sortable: true,
      value: (a) => a.created_at || "",
      render: (a) => (a.created_at ? formatRelative(a.created_at) : "—"),
    },
    {
      key: "last_used_at",
      label: "Last used",
      sortable: true,
      value: (a) => a.last_used_at || "",
      // "never" is the actionable value here — an app password created and never used is one
      // that can be revoked with no consequence, and a blank cell would hide that.
      render: (a) => (a.last_used_at ? formatRelative(a.last_used_at) : <span className="text-muted">never</span>),
    },
  ];

  return (
    <div className="space-y-4">
      {confirm.dialog}
      {/* PASSWORDS. A mailbox has two, and the difference is the thing people get wrong: the MAIL
          password is what mail apps use; the SIGN-IN password is for this app. Each card says which
          in one line; the rest is a tap away. */}
      <PasswordPair
        title="Mail password"
        minLength={10}
        currentLabel="Current mail password"
        hint={
          <>
            Used by mail apps such as Apple Mail and Outlook — not for signing in here.
            <Details summary="What changes" className="mt-1">
              <p>Changing it signs those apps out until you enter the new password in each of them.</p>
            </Details>
          </>
        }
        onSave={(c, n) => api.setMailPassword(c, n)}
      />

      {/* The sign-in password AND two-factor, from the shared kit — one implementation for every
          console (QR, same-device link, recovery codes, a way to turn it off). */}
      <PasswordAndTwoFactor passwordTitle="Sign-in password" passwordHint="Used to sign in to this app. Changing it does not affect your mail apps." />

      <Section
        title="App passwords"
        hint={appPw.data ? `${appPw.data.total}` : undefined}
        actions={
          appPw.data?.max != null ? (
            <span className="text-[12.5px] text-muted">
              {(appPw.data.items ?? []).length} of {appPw.data.max} used
            </span>
          ) : undefined
        }
      >
        <p className="text-[13px] leading-relaxed text-muted">
          {/* The server's own sentence, which says the thing that matters: each device signs in with
              its OWN username, not the mailbox address. */}
          {appPw.data?.note ?? "One per app or device, so you can sign out one phone without changing your password."}
        </p>
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <div className="min-w-0 flex-[1_1_12rem]">
            <Field label="New app password for" placeholder="e.g. iPhone Mail" value={newAppName} onChange={setNewAppName} />
          </div>
          <Button
            variant="accent"
            // At the cap the server refuses, so the button must not invite it.
            disabled={!newAppName.trim() || (appPw.data?.max != null && (appPw.data.items ?? []).length >= appPw.data.max)}
            onClick={async () => {
              try {
                const r = await api.createAppPassword(newAppName.trim());
                setNewAppName("");
                void qc.invalidateQueries({ queryKey: ["app-passwords"] });
                // Shown ONCE, held on screen until dismissed. The username matters as much as the
                // password: an app password is used with localpart+tag@domain, NOT the mailbox's own
                // address, so a password shown alone is unusable.
                if (r?.password) setRevealed({ what: "App password", value: r.password, username: r.username, note: r.note });
              } catch (e) {
                notify({ kind: "error", title: "Could not create", body: e instanceof Error ? e.message : String(e) });
              }
            }}
          >
            Create
          </Button>
        </div>
        <div className="mt-4">
          <DataList
            items={appPw.data?.items ?? []}
            columns={appCols}
            getRowKey={(a) => String(a.id)}
            loading={appPw.isLoading}
            error={appPw.error}
            errorContext="your app passwords"
            emptyMessage="No app passwords."
            rowActions={(a) => (
              <Button
                size="sm"
                variant="danger"
                onClick={() =>
                  confirm.ask(
                    {
                      title: `Revoke the app password "${a.name}"?`,
                      body: "The app using it is signed out and cannot collect mail until it gets a new one.",
                      confirmLabel: "Revoke",
                    },
                    () => act("App password revoked", () => api.deleteAppPassword(a.id), ["app-passwords"]),
                  )
                }
              >
                Revoke
              </Button>
            )}
          />
        </div>
      </Section>

      {/* SIGNED IN & CONNECTED: browser sign-ins to this app, then mail apps connected right now.
          Two different things, deliberately listed apart — signing out of this app does not
          disconnect a mail app. */}
      {/* A phone signed in with one scan; it then appears in the list below as a device of its own. */}
      <PhoneSetup path="/auth/pair" sessionsPath="/auth/sessions" title="Sign in a phone with a code" />
      <SessionsPanel />
      <Section title="Mail apps connected now" hint={devices.data ? `${devices.data.total}` : undefined}>
        <p className="text-[13px] text-muted">Don't recognise one? Change your mail password — that signs every app out.</p>
        <div className="mt-3">
          <DataList
            items={devices.data?.items ?? []}
            columns={deviceCols}
            // A live connection has no id of its own, so the key is what identifies it.
            getRowKey={(d: Device) => `${d.service}-${d.ip}-${d.user_agent ?? ""}`}
            loading={devices.isLoading}
            error={devices.error}
            errorContext="your connected apps"
            emptyMessage="Nothing is connected right now."
          />
        </div>
      </Section>

      {/* ADVANCED: an ACCESS TOKEN signs a mail app or service into this mailbox without the
          password; an API KEY authenticates a script against this app as you. Two endpoints, two
          field names (/tokens/me takes {label}, /auth/tokens takes {name}); most people need
          neither, so they are folded away. */}
      <Section title="Advanced: tokens and API keys" collapsible defaultOpen={false} id="mb-advanced-tokens" hint="for integrations">
        <div className="space-y-6">
          <TokenSection
            title="Access tokens"
            hint="Sign a mail app or service in without your password. Anyone holding one can read your mail — treat it like a password."
            items={(myTokens.data?.tokens ?? []).map((t) => ({
              revokeKey: t.jti,
              name: t.label ?? "",
              created_at: t.created_at,
              last_used_at: t.last_used_at,
              jti: t.jti,
              revoked_at: t.revoked_at,
            }))}
            loading={myTokens.isLoading}
            error={myTokens.error}
            onCreate={async (v) => {
              const r = await api.createMyToken(v);
              void qc.invalidateQueries({ queryKey: ["my-tokens"] });
              if (r?.token) setRevealed({ what: "Access token", value: r.token });
            }}
            onRevoke={async (jti) => {
              await api.revokeMyToken(jti);
              void qc.invalidateQueries({ queryKey: ["my-tokens"] });
            }}
          />
          <TokenSection
            title="API keys"
            hint="For scripts that act as you in this app. Each is shown once when created."
            items={(tokens.data?.items ?? []).map((t) => ({
              revokeKey: String(t.id),
              name: t.name ?? "",
              created_at: t.created_at,
              last_used_at: t.last_used_at,
            }))}
            loading={tokens.isLoading}
            error={tokens.error}
            onCreate={async (v) => {
              const r = await api.createAuthToken(v);
              void qc.invalidateQueries({ queryKey: ["auth-tokens"] });
              if (r?.token) setRevealed({ what: "API key", value: r.token });
            }}
            onRevoke={async (id) => {
              await api.deleteAuthToken(Number(id));
              void qc.invalidateQueries({ queryKey: ["auth-tokens"] });
            }}
          />
        </div>
      </Section>

      {revealed && (
        <SecretReveal
          title={`${revealed.what} created`}
          secret={revealed.value}
          username={revealed.username}
          note={revealed.note}
          onClose={() => setRevealed(null)}
        />
      )}
    </div>
  );
}

/**
 * ONE row shape for this card, which both callers map into.
 *
 * It used to take `ApiToken[]`, a single interface covering rows from two different tables:
 * /auth/tokens (keyed on `id`, named `name`) and /tokens/me (keyed on `jti`, named `label`). The
 * card revoked with `t.id` — a field /tokens/me rows do not carry — so every Revoke on the
 * Access tokens card posted to `/tokens/me/undefined/revoke`. It typechecked the whole time,
 * because the union declared `id` as present on rows that never have it.
 *
 * A card that has to guess which of two shapes it was handed will eventually guess wrong. The
 * caller knows, so the caller says: it supplies `revokeKey`, already correct for its endpoint.
 */
export interface TokenRow {
  /** What the revoke route for THIS list takes — an id for API keys, a jti for access tokens. */
  revokeKey: string;
  name: string;
  created_at: string;
  last_used_at?: string | null;
  /** Present only for mailbox access tokens, and only those can offer an Apple Mail profile. */
  jti?: string;
  revoked_at?: string | null;
}

function TokenSection({
  title,
  hint,
  items,
  loading,
  error,
  onCreate,
  onRevoke,
}: {
  title: string;
  hint: string;
  items: TokenRow[];
  loading: boolean;
  error: unknown;
  onCreate: (name: string) => Promise<void>;
  onRevoke: (revokeKey: string) => Promise<void>;
}) {
  const { notify } = useToast();
  const confirm = useConfirm();
  const [name, setName] = React.useState("");

  const cols: DataListColumn<TokenRow>[] = [
    { key: "name", label: "Name", sortable: true, render: (t) => t.name || <span className="text-muted">unnamed</span> },
    { key: "created_at", label: "Created", sortable: true, value: (t) => t.created_at || "", render: (t) => (t.created_at ? formatRelative(t.created_at) : "—") },
    { key: "last_used_at", label: "Last used", sortable: true, value: (t) => t.last_used_at || "", render: (t) => (t.last_used_at ? formatRelative(t.last_used_at) : <span className="text-muted">never</span>) },
    {
      // v1 offered this beside every live token and v2 dropped it: GET /tokens/me/{jti}/apple-mail
      // returns a .mobileconfig that installs this mailbox on an iPhone or Mac with no password
      // typed, because the profile carries the token itself.
      //
      // Only for a token that HAS a jti and is NOT revoked. The profile embeds the credential, so
      // offering one for a revoked token installs an account that cannot sign in — and the person
      // would blame the mail app, not the dead token.
      key: "apple",
      label: "Apple Mail",
      render: (t) =>
        t.jti && !t.revoked_at ? (
          <a
            className="text-accent hover:underline"
            href={`/api/v1/tokens/me/${encodeURIComponent(t.jti)}/apple-mail`}
            title="Install this mailbox in Apple Mail (iPhone or Mac)"
          >
            profile
          </a>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
  ];

  return (
    <div>
      {confirm.dialog}
      <div className="text-[14px] font-medium">{title}</div>
      <p className="mt-0.5 text-[13px] leading-relaxed text-muted">{hint}</p>
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <div className="min-w-0 flex-[1_1_12rem]">
          <Field label="Name" placeholder="what it is for" value={name} onChange={setName} />
        </div>
        <Button
          disabled={!name.trim()}
          onClick={async () => {
            try {
              await onCreate(name.trim());
              setName("");
            } catch (e) {
              notify({ kind: "error", title: "Could not create", body: e instanceof Error ? e.message : String(e) });
            }
          }}
        >
          Create
        </Button>
      </div>
      <div className="mt-3">
        <DataList
          items={items}
          columns={cols}
          getRowKey={(t) => t.revokeKey}
          loading={loading}
          error={error}
          errorContext="your keys"
          emptyMessage="None yet."
          rowActions={(t) => (
            <Button
              size="sm"
              variant="danger"
              onClick={() =>
                confirm.ask(
                  { title: `Revoke "${t.name || "this key"}"?`, body: "Anything using it stops working at once.", confirmLabel: "Revoke" },
                  async () => {
                    try {
                      await onRevoke(t.revokeKey);
                      notify({ kind: "success", title: "Revoked" });
                    } catch (e) {
                      notify({ kind: "error", title: "Could not revoke", body: e instanceof Error ? e.message : String(e) });
                    }
                  },
                )
              }
            >
              Revoke
            </Button>
          )}
        />
      </div>
    </div>
  );
}
